// src/components/Footer.js
import React from "react";
import styled from "styled-components";
import { Link } from "react-scroll";
import { motion } from "framer-motion";
import { FaFacebookF, FaInstagram, FaTwitter, FaYoutube, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaArrowUp } from "react-icons/fa";
import logoWhite from "../assets/logo-white.png"; // You'll need to create/add this

const FooterContainer = styled.footer`
  background: #000;
  padding: 80px 0 0;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, #ff9900, #ff6600, #00ffcc, #ff9900);
    background-size: 300% 100%;
    animation: gradient 5s ease infinite;
  }
  
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div``;

const FooterLogo = styled.div`
  margin-bottom: 25px;
  
  img {
    height: 60px;
  }
`;

const FooterAbout = styled.p`
  font-size: 16px;
  color: #ccc;
  line-height: 1.7;
  margin-bottom: 25px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff9900;
    color: #000;
  }
`;

const FooterTitle = styled.h3`
  font-size: 20px;
  font-family: 'Orbitron', sans-serif;
  color: #ff9900;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 25px;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background: #ff9900;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 15px;
  
  a {
    color: #ccc;
    font-size: 16px;
    transition: all 0.3s ease;
    display: inline-block;
    
    &:hover {
      color: #ff9900;
      transform: translateX(5px);
    }
  }
`;

const ContactInfo = styled.div``;

const ContactItem = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 153, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 15px;
  color: #ff9900;
  font-size: 16px;
  flex-shrink: 0;
`;

const ContactText = styled.p`
  font-size: 15px;
  color: #ccc;
  line-height: 1.5;
`;



const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 25px 0;
  text-align: center;
`;

const Copyright = styled.p`
  color: #ccc;
  font-size: 14px;
  
  a {
    color: #ff9900;
    font-weight: bold;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ScrollTopButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background: linear-gradient(45deg, #ff9900, #ff6600);
  color: #000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  box-shadow: 0 5px 15px rgba(255, 153, 0, 0.3);
  z-index: 99;
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <FooterContainer>
      <Container>
        <FooterTop>
          <FooterColumn>
            <FooterLogo>
              <img src={logoWhite} alt="Hopito Logo" />
            </FooterLogo>
            <FooterAbout>
              Experience the ultimate fusion of taste and technology at Hopito.
              Our innovative restaurant combines futuristic dining with
              classic culinary excellence.
            </FooterAbout>
            <SocialLinks>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaFacebookF />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaYoutube />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <Link to="home" smooth={true} duration={500}>Home</Link>
              </FooterLink>
              <FooterLink>
                <Link to="about" smooth={true} duration={500}>About Us</Link>
              </FooterLink>
              <FooterLink>
                <Link to="menu" smooth={true} duration={500}>Our Menu</Link>
              </FooterLink>
              <FooterLink>
                <Link to="gallery" smooth={true} duration={500}>Gallery</Link>
              </FooterLink>
              <FooterLink>
                <Link to="contact" smooth={true} duration={500}>Contact</Link>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Contact Info</FooterTitle>
            <ContactInfo>
              <ContactItem>
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactText>
                  Opposite Vence Paradise, Danapur, Patna, Bihar, India
                </ContactText>
              </ContactItem>
              <ContactItem>
                <ContactIcon>
                  <FaPhoneAlt />
                </ContactIcon>
                <ContactText>+91 96725 65772</ContactText>
              </ContactItem>
              <ContactItem>
                <ContactIcon>
                  <FaEnvelope />
                </ContactIcon>
                <ContactText>rajattak7@gmail.com</ContactText>
              </ContactItem>
            </ContactInfo>
          </FooterColumn>

          {/* <FooterColumn>
            <FooterTitle>Newsletter</FooterTitle>
            <Newsletter>
              <NewsletterText>
                Subscribe to our newsletter to receive the latest updates and exclusive offers.
              </NewsletterText>
              <SubscribeForm>
                <SubscribeInput 
                  type="email" 
                  placeholder="Enter your email" 
                />
                <SubscribeButton
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Subscribe
                </SubscribeButton>
              </SubscribeForm>
            </Newsletter>
          </FooterColumn> */}
        </FooterTop>

        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} <a href="/">Hopito</a>. All Rights Reserved. Designed with ❤️
          </Copyright>
        </FooterBottom>
      </Container>

      <ScrollTopButton
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FaArrowUp />
      </ScrollTopButton>
    </FooterContainer>
  );
};

export default Footer;